import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Fingerprint, FileSignature, ChevronLeft, ChevronRight, Lightbulb,
  Mail, CheckCircle2, ListChecks,
} from "lucide-react";

/**
 * Guia interativo "Como fazer" — Biometria & Aceite Digital (DocuSign).
 * Conteúdo dos passo a passo oficiais da Prime (uso interno), no formato de
 * estudo do Catálogo: índice navegável à esquerda, passo grande à direita,
 * setas/teclado para avançar. Aberto de dentro da Triagem Biometria — o
 * consultor consulta ENQUANTO orienta o cliente no telefone.
 */

interface Passo {
  titulo: string;
  texto: string;
  dica?: string;
  remetente?: string;
  chips?: string[];
}

interface Guia {
  id: "biometria" | "aceite";
  nome: string;
  icone: typeof Fingerprint;
  objetivo: string;
  quando: string;
  passos: Passo[];
  resumo: string[];
}

const GUIAS: Guia[] = [
  {
    id: "biometria",
    nome: "Biometria",
    icone: Fingerprint,
    objetivo: "Orientar o cliente a concluir a validação de identidade exigida pela Claro Empresas para finalizar o pedido.",
    quando: "Sempre que um pedido ficar pendente de biometria — o cliente recebe o link por e-mail ou WhatsApp.",
    passos: [
      {
        titulo: "Acessar o link da biometria",
        texto: "O cliente recebe o link de duas formas: por e-mail com assunto \"Biometria\" ou pelo WhatsApp, enviado pelo consultor. No e-mail, clicar no botão \"CLIQUE AQUI\" para iniciar.",
        remetente: "claroempresasbiometria@minhaclaro.com.br",
      },
      {
        titulo: "Confirmar identidade (CPF + reCAPTCHA)",
        texto: "Informar os 5 primeiros dígitos do CPF, validar o reCAPTCHA (\"I'm not a robot\") e clicar em \"Verificar\".",
      },
      {
        titulo: "Autenticação facial",
        texto: "O sistema mostra as instruções da biometria facial. Seguir o passo a passo da tela e clicar em \"Avançar\".",
        dica: "Oriente o cliente: local bem iluminado, tirar óculos e boné, câmera na direção do rosto.",
      },
      {
        titulo: "Escanear ou anexar documento (se solicitado)",
        texto: "Se o sistema pedir, o cliente escolhe Documento Físico (CNH, RG, DNI, CIN, RNE, RNM ou Passaporte) ou Digital (CDT — Carteira Digital de Trânsito, ou RG/DNI/CIN em PDF), escaneia ou anexa e clica em \"Avançar\".",
        chips: ["CNH", "RG", "DNI", "CIN", "RNE", "RNM", "Passaporte", "CDT digital"],
      },
      {
        titulo: "Conclusão — \"Tudo pronto!\"",
        texto: "Ao finalizar todas as etapas, aparece a mensagem \"Tudo pronto!\" confirmando o recebimento. A biometria está concluída.",
        dica: "Depois de concluir, use \"Comercial sinalizou\" aqui na Triagem — o BKO confere e move o pedido.",
      },
    ],
    resumo: [
      "Acessar o link recebido por e-mail ou WhatsApp",
      "Informar os 5 primeiros dígitos do CPF e validar o reCAPTCHA",
      "Seguir o passo a passo da autenticação facial",
      "Escanear/anexar o documento, se solicitado",
      "Aguardar a confirmação \"Tudo pronto!\"",
    ],
  },
  {
    id: "aceite",
    nome: "Aceite Digital",
    icone: FileSignature,
    objetivo: "Orientar o cliente a concluir a assinatura eletrônica do contrato via DocuSign, exigida pela Claro Empresas.",
    quando: "Sempre que um contrato for enviado para assinatura — o cliente recebe o link por e-mail.",
    passos: [
      {
        titulo: "Acessar o e-mail e revisar os documentos",
        texto: "O cliente recebe e-mail com assunto \"Assinatura de contrato - Claro Empresas\". Clicar no botão amarelo \"REVISAR DOCUMENTOS\".",
        remetente: "documentosdigitais@sign.claroassina.com.br",
      },
      {
        titulo: "Permitir localização e aceitar os termos",
        texto: "É obrigatório permitir a localização solicitada pelo navegador. Depois, marcar \"Concordo em usar assinaturas e registros eletrônicos\" e clicar em \"Continuar\".",
        dica: "Se o cliente negar a localização, a assinatura não avança — pedir para recarregar e permitir.",
      },
      {
        titulo: "Iniciar a assinatura",
        texto: "Na tela \"Analisar e preencher\", o contrato PME é exibido. Clicar no botão amarelo \"Iniciar\", no canto superior esquerdo.",
      },
      {
        titulo: "Ir até a primeira rubrica",
        texto: "O sistema leva automaticamente à primeira rubrica. Clicar no botão azul \"Rubricar\".",
      },
      {
        titulo: "Adotar a rubrica e assinatura",
        texto: "Na janela \"Adote sua rubrica\", confirmar o Nome Completo e a Rubrica, conferir o estilo gerado e clicar em \"Adotar e rubricar\".",
      },
      {
        titulo: "Assinar em todos os locais indicados",
        texto: "Clicar em TODOS os pontos indicados com botão azul — o sistema conduz de um a um até o fim.",
        dica: "Se pular algum ponto, o \"Concluir\" não libera. O botão azul sempre mostra onde falta.",
      },
      {
        titulo: "Concluir a assinatura",
        texto: "Com tudo assinado/rubricado, clicar no botão amarelo \"Concluir\", no canto superior direito.",
      },
      {
        titulo: "Confirmação e salvamento",
        texto: "Aparece \"Você assinou o documento com sucesso\". A cópia vai por e-mail quando todos assinarem. Para salvar na hora: ícone de impressora (canto superior direito) e depois \"CONCLUÍDO\".",
      },
    ],
    resumo: [
      "Acessar o e-mail e clicar em \"REVISAR DOCUMENTOS\"",
      "Permitir localização e aceitar os termos (\"Continuar\")",
      "Clicar em \"Iniciar\"",
      "Botão azul de rubrica → \"Adotar e rubricar\"",
      "Clicar em todos os locais indicados",
      "Clicar em \"Concluir\"",
      "Salvar pelo ícone de impressora → \"CONCLUÍDO\"",
    ],
  },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guiaInicial?: "biometria" | "aceite";
}

export function GuiaBiometriaAceite({ open, onOpenChange, guiaInicial = "biometria" }: Props) {
  const [guiaId, setGuiaId] = useState<"biometria" | "aceite">(guiaInicial);
  const [passo, setPasso] = useState(0); // índice; passos.length = Resumo

  const guia = useMemo(() => GUIAS.find((g) => g.id === guiaId)!, [guiaId]);
  const totalTelas = guia.passos.length + 1;
  const noResumo = passo >= guia.passos.length;

  useEffect(() => { if (open) { setGuiaId(guiaInicial); setPasso(0); } }, [open, guiaInicial]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setPasso((p) => Math.min(p + 1, totalTelas - 1));
      if (e.key === "ArrowLeft") setPasso((p) => Math.max(p - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, totalTelas]);

  const trocarGuia = (id: "biometria" | "aceite") => { setGuiaId(id); setPasso(0); };
  const p = noResumo ? null : guia.passos[passo];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-3 border-b bg-muted/30 space-y-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <guia.icone className="h-4 w-4" />
              </span>
              Como fazer — passo a passo para orientar o cliente
            </DialogTitle>
            <div className="inline-flex items-center gap-1 p-0.5 rounded-full border bg-card">
              {GUIAS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => trocarGuia(g.id)}
                  className={cn(
                    "text-xs px-3 py-1 rounded-full transition-colors flex items-center gap-1.5",
                    guiaId === g.id ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <g.icone className="h-3 w-3" />
                  {g.nome}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground pt-2">
            <b>Quando usar:</b> {guia.quando}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-[200px_1fr] max-h-[62vh]">
          {/* Índice à esquerda — padrão do Catálogo */}
          <nav className="border-r bg-muted/20 overflow-y-auto py-2">
            {guia.passos.map((pp, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPasso(i)}
                className={cn(
                  "w-full text-left px-3 py-2 flex items-start gap-2 text-xs transition-colors border-l-2",
                  i === passo && !noResumo
                    ? "border-primary bg-primary/5 text-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40",
                )}
              >
                <span
                  className={cn(
                    "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0",
                    i === passo && !noResumo ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                  )}
                >
                  {i + 1}
                </span>
                <span className="leading-snug">{pp.titulo}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPasso(guia.passos.length)}
              className={cn(
                "w-full text-left px-3 py-2 flex items-center gap-2 text-xs transition-colors border-l-2",
                noResumo
                  ? "border-primary bg-primary/5 text-foreground font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40",
              )}
            >
              <span className={cn(
                "h-5 w-5 rounded-full flex items-center justify-center shrink-0",
                noResumo ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
              )}>
                <ListChecks className="h-3 w-3" />
              </span>
              Resumo rápido
            </button>
          </nav>

          {/* Conteúdo do passo */}
          <div className="overflow-y-auto p-5">
            {p ? (
              <div className="space-y-3">
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-primary/20 tabular-nums leading-none">
                    {String(passo + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-lg font-semibold leading-tight">{p.titulo}</h3>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed">{p.texto}</p>

                {p.remetente && (
                  <div className="flex items-center gap-2 text-xs bg-muted/50 border rounded-lg px-3 py-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground">Remetente oficial:</span>
                    <code className="tabular-nums text-foreground break-all">{p.remetente}</code>
                  </div>
                )}

                {p.chips && (
                  <div className="flex flex-wrap gap-1">
                    {p.chips.map((c) => (
                      <Badge key={c} variant="outline" className="text-[10px] font-normal">{c}</Badge>
                    ))}
                  </div>
                )}

                {p.dica && (
                  <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
                    <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{p.dica}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <ListChecks className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Resumo rápido — {guia.nome}</h3>
                </div>
                <ul className="space-y-1.5">
                  {guia.resumo.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground pt-1">{guia.objetivo}</p>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-2.5 border-t bg-muted/30 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            {noResumo ? "Resumo" : `Passo ${passo + 1} de ${guia.passos.length}`} · use ← → no teclado
          </span>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={passo === 0} onClick={() => setPasso((v) => v - 1)}>
              <ChevronLeft className="h-3.5 w-3.5 mr-0.5" /> Anterior
            </Button>
            <Button size="sm" className="h-7 px-2 text-xs" disabled={passo >= totalTelas - 1} onClick={() => setPasso((v) => v + 1)}>
              Próximo <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
